import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { cn, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useNavigate, useParams } from "react-router";
import { useErrorMessage } from "../../../lib/api-errors";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { COMPOSER_ID } from "./composer";
import { ConversationList, type ListData } from "./conversation-list";
import { inboxKeys, useConversationActions, useConversationList } from "./inbox-api";
import { arrangeByPriority, type ConversationItem } from "./inbox-model";
import { useInboxStore } from "./inbox-store";
import { useInboxQueue } from "./queue-sort-api";
import { orderByQueue, ordersByQueue } from "./queue-sort";
import { ShortcutLegend } from "./shortcut-legend";
import { ThreadPane } from "./thread-pane";
import { useInboxShortcuts } from "./use-inbox-shortcuts";
import { useInboxStream, type InboxStreamEvent } from "./use-inbox-stream";

/** Fallback polling while the SSE stream is down (CLAUDE.md rule 8: SSE with polling fallback). */
const FALLBACK_POLL_MS = 10_000;
const DESKTOP_QUERY = "(min-width: 1024px)";

function useIsDesktop(): boolean {
  return useSyncExternalStore(
    (notify) => {
      const query = window.matchMedia(DESKTOP_QUERY);
      query.addEventListener("change", notify);
      return () => { query.removeEventListener("change", notify); };
    },
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => true,
  );
}

/** Focuses the reply box, or the "Take over" button while the AI still owns the chat. */
function focusComposer(): void {
  const composer = document.getElementById(COMPOSER_ID);
  if (composer instanceof HTMLTextAreaElement && !composer.disabled) {
    composer.focus();
    return;
  }
  document.querySelector<HTMLButtonElement>("[data-testid=composer-lock] button")?.focus();
}

function useListData(pollMs: number | false): ListData {
  const filters = useInboxStore((s) => s.filters);
  const list = useConversationList(filters, pollMs);
  const byQueue = ordersByQueue(filters.view, filters.sort);
  const queue = useInboxQueue(filters.view === "needs_staff");
  return useMemo(
    () => ({
      items: ((arranged) => (byQueue && queue ? orderByQueue(arranged, queue.entries) : arranged))(
        arrangeByPriority<ConversationItem>(list.data?.pages.flatMap((p) => p.items) ?? [], filters),
      ),
      queue: filters.view === "needs_staff" ? queue : null,
      counts: list.data?.pages[0]?.counts ?? {},
      isPending: list.isPending,
      isError: list.isError,
      hasMore: list.hasNextPage,
      loadingMore: list.isFetchingNextPage,
      retry: () => void list.refetch(),
      loadMore: () => void list.fetchNextPage(),
    }),
    [list, filters, byQueue, queue],
  );
}

/** `/w/:ws/inbox/:conversationId?` — flush, full-height 3-pane inbox with live updates. */
export function InboxPage() {
  const { t } = useTranslation();
  const { conversationId = null } = useParams();
  const { summary } = useWorkspaceContext();
  const wsId = useWorkspaceId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const isDesktop = useIsDesktop();
  const setComposerMode = useInboxStore((s) => s.setComposerMode);

  const onEvent = useCallback(
    (event: InboxStreamEvent) => {
      void queryClient.invalidateQueries({ queryKey: [...inboxKeys.root(wsId), "list"] });
      void queryClient.invalidateQueries({ queryKey: inboxKeys.detail(wsId, event.conversation_id) });
    },
    [queryClient, wsId],
  );
  const stream = useInboxStream(onEvent);
  const list = useListData(stream === "live" ? false : FALLBACK_POLL_MS);
  const actions = useConversationActions(conversationId ?? "");

  const open = useCallback((id: string | null, replace = false) => { void navigate(consoleRoutes.inbox(summary.slug, id ?? undefined), { replace }); }, [navigate, summary.slug]);
  const firstId = list.items[0]?.id ?? null;
  useEffect(() => {
    if (isDesktop && !conversationId && firstId) open(firstId, true);
  }, [isDesktop, conversationId, firstId, open]);

  const step = (delta: number) => {
    const index = list.items.findIndex((i) => i.id === conversationId);
    const next = list.items[Math.min(Math.max(index + delta, 0), list.items.length - 1)];
    if (next) open(next.id);
  };
  useInboxShortcuts({
    next: () => { step(1); },
    previous: () => { step(-1); },
    reply: () => {
      setComposerMode("reply");
      setTimeout(focusComposer, 0);
    },
    resolve: () => {
      if (!conversationId) return;
      actions.setStatus.mutateAsync("resolved").then(
        () => toast.success(t("engage.inbox.shortcuts.resolved")),
        (error: unknown) => toast.error(errorMessage(error)),
      );
    },
  });

  return (
    <div className="-mx-4 -my-6 flex h-[calc(100dvh-3.5rem)] min-h-0 overflow-hidden lg:-mx-8 lg:-my-8" data-testid="inbox-page">
      <div className={cn("flex min-h-0 w-full flex-col lg:w-80 lg:shrink-0", conversationId && "max-lg:hidden")}>
        <ConversationList data={list} selectedId={conversationId} stream={stream} className="flex-1 lg:w-80" />
        <ShortcutLegend />
      </div>
      <ThreadPane conversationId={conversationId} onBack={() => { open(null); }} className={cn(!conversationId && "max-lg:hidden")} />
    </div>
  );
}
